/**
 * /403 落地页的原因参数(0926 修复波 #10)。
 *
 * ProtectedRoute 的 module 门拦下租户角色时,缺的是「公司没开通这个功能」,不是「你的角色无权」——
 * 两种情况给用户的下一步完全不同(前者找我们开通,后者找自己公司管理员),不能共用一句话。
 */
export const FORBIDDEN_REASON_PARAM = "reason";
export const FORBIDDEN_REASON_MODULE_NOT_ENABLED = "module-not-enabled";
/** OEM 第二批 C4:模块已拥有但被服务商暂停(后端 MODULE_NOT_ENABLED + details `reason=PAUSED`)。 */
export const FORBIDDEN_REASON_MODULE_PAUSED = "module-paused";

/**
 * 1005:模块「拥有过、现在关着、又不是被暂停」= 因套餐 / 增值舱到期(或退款)关闭 —— 不能说「还没开通」。
 * 判据见 {@link lapsedModulesOf}。
 */
export const FORBIDDEN_REASON_MODULE_EXPIRED = "module-expired";

export const FORBIDDEN_MODULE_NOT_ENABLED_PATH = `/403?${FORBIDDEN_REASON_PARAM}=${FORBIDDEN_REASON_MODULE_NOT_ENABLED}`;
export const FORBIDDEN_MODULE_EXPIRED_PATH = `/403?${FORBIDDEN_REASON_PARAM}=${FORBIDDEN_REASON_MODULE_EXPIRED}`;
export const FORBIDDEN_MODULE_PAUSED_PATH = `/403?${FORBIDDEN_REASON_PARAM}=${FORBIDDEN_REASON_MODULE_PAUSED}`;

/**
 * OEM H1:模块未开通 / 已停用 / 已过期时,历史作品仍可看、可下载、可删,但新建 / 消耗积分的动作不可用。
 * 置灰按钮、只读横幅、点击拦截统一用这一句(与后端 MODULE_NOT_ENABLED 文案同调)。
 */
export const MODULE_LOCKED_NOTICE = "该功能未开通,如需继续使用请联系管理员";

/** 被服务商暂停的舱:403 页标题 / toast 标题共用这一句。 */
export const MODULE_PAUSED_TITLE = "该功能已被服务商暂停";

/** OEM 第二批 A7:历史只读态里「已拥有但被服务商暂停」的那句(/me/permissions.pausedModules)。 */
export const MODULE_PAUSED_NOTICE = "该功能已被服务商暂停,如需继续使用请联系服务商或管理员";

/**
 * 只读提示:被暂停(pausedModules 里)说「已被服务商暂停」,其余说「未开通」。
 * 老后端不带 pausedModules → 一律「未开通」,不猜。
 */
export function moduleLockedNotice(
  moduleKey: string,
  pausedModules: readonly string[] | null | undefined
): string {
  return pausedModules?.includes(moduleKey) ? MODULE_PAUSED_NOTICE : MODULE_LOCKED_NOTICE;
}

/**
 * MODULE_NOT_ENABLED 的原因(OEM 第二批 A2):
 *   · PAUSED    = 已拥有、被服务商 / 平台暂停;
 *   · NOT_OWNED = 未开通。
 * ★只有 H5 舱门拦截器与 requireEnabled 路径带 `reason=…`;部分 service(geo / maplead / acq / reporter /
 *   website / advisor)自抛时不带 → 缺 reason 一律按「未开通」,绝不猜成暂停。
 */
export type ModuleBlockReason = "PAUSED" | "NOT_OWNED";

/** 从后端 ErrorResponse.details(`["reason=PAUSED"]`)里读原因;缺省 = NOT_OWNED。 */
export function moduleBlockReasonFromDetails(
  details: readonly string[] | null | undefined
): ModuleBlockReason {
  return (details ?? []).some((d) => d.trim() === "reason=PAUSED") ? "PAUSED" : "NOT_OWNED";
}

/**
 * 任意 throwable → 舱被拦的原因;不是 MODULE_NOT_ENABLED 时返回 null。
 * 鸭子类型读 errorCode / details,不 import ApiError(shared/lib 不反向依赖 shared/api 的类)。
 */
export function moduleBlockReasonOf(e: unknown): ModuleBlockReason | null {
  if (!e || typeof e !== "object") return null;
  const err = e as { errorCode?: unknown; details?: unknown };
  if (err.errorCode !== "MODULE_NOT_ENABLED") return null;
  return moduleBlockReasonFromDetails(
    Array.isArray(err.details) ? (err.details as string[]) : undefined
  );
}

/** 被拦原因 → 403 落地页路径。 */
export function forbiddenPathFor(reason: ModuleBlockReason): string {
  return reason === "PAUSED" ? FORBIDDEN_MODULE_PAUSED_PATH : FORBIDDEN_MODULE_NOT_ENABLED_PATH;
}

/**
 * 1005:因到期关闭的舱 = /me/permissions 的 `everOwnedModules`(历史上拥有过、现在锁着)− `pausedModules`
 * (被服务商暂停的另说)。到期扫描把订阅转 expired 时会把门控权益全关,但权益行仍在 → 正好落在这里。
 * ★老后端没有 everOwnedModules → 空(一律按「未开通」,不猜)。退款后关闭的舱也会落在这里(同样不是「没买过」)。
 */
export function lapsedModulesOf(
  perms:
    | { everOwnedModules?: readonly string[] | null; pausedModules?: readonly string[] | null }
    | null
    | undefined
): string[] {
  const paused = new Set(perms?.pausedModules ?? []);
  return (perms?.everOwnedModules ?? []).filter((m) => !paused.has(m));
}
